import {
    fetchLedgerAccount,
    fetchLedgerEntries,
    generateInvestorsLedgerStatements,
    getBankTransactions,
    getInvestments,
    getInvestorLedgerStatements,
    getNamedBankAccount,
    getTenantLedgerStatements,
} from '@/api/finance.api';
import { recoverInvestorLedgerAsync } from '@/api/property-management.api';
import { showMessage, withLoading } from '@/shared';
import type {
    BankAccountExpandOpts,
    BankTransaction,
    BankTransactionExpandOpts,
    GeneralLedgerExpandOpts,
    GetBankTransactionParams,
    IBaseLedgerEntry,
    IFindEstateInvestmentsParams,
    IGeneralLedger,
    IPaginationFields,
    LedgerEntryExpandOpts,
} from '@condo/domain';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, computed, toRef, unref } from 'vue';

export const useInvestmentCases = (params: MaybeRef<IFindEstateInvestmentsParams>) => {
    return useQuery(['investments', toRef(params)], () => getInvestments(unref(params)), {
        initialData: [],
    });
};

export const useLedgerAccount = (data: MaybeRef<{ query: Partial<IGeneralLedger>; expandOpts?: GeneralLedgerExpandOpts }>) => {
    const { query, expandOpts } = unref(data);
    return useQuery(['ledgerAccount', query], () => fetchLedgerAccount(query, expandOpts));
};

export const useFindLedgerEntries = (
    params: MaybeRef<{
        ledgerQuery: Partial<
            Pick<IGeneralLedger, 'generalLedgerId' | 'loanAccountId' | 'bankAccountId' | 'bookKeepingId' | 'generalLedgerType' | 'rentTenancyId'>
        >;
        entriesQuery?: Partial<Pick<IBaseLedgerEntry, 'entryGroup' | 'entryGroupType' | 'generalLedgerId' | 'investorInfoId' | 'mfhId'>> & { year?: number };
        expandOpts?: LedgerEntryExpandOpts;
        pagination?: IPaginationFields;
    }>,
) => {
    return useQuery<Awaited<ReturnType<typeof fetchLedgerEntries>>>(['fetchLedgerEntries', params], () => {
        const { ledgerQuery, entriesQuery, expandOpts, pagination } = unref(params);
        return fetchLedgerEntries(ledgerQuery, entriesQuery, expandOpts, pagination);
    });
};

export const useBankTransactions = (
    query: GetBankTransactionParams,
    { size, page, order, orderBy }: { size: number; page: number; order: 'DESC' | 'ASC'; orderBy: string } = {
        size: 10,
        page: 1,
        order: 'DESC',
        orderBy: 'valueDate',
    },
    expandOpts?: BankTransactionExpandOpts,
) => {
    return useInfiniteQuery(
        ['bank-transactions', query],
        async ({
            pageParam = {
                page,
                size,
            },
        }): Promise<{ bankTransactions: BankTransaction[]; counter: number }> => {
            return getBankTransactions(query, expandOpts, {
                page: pageParam.page,
                size: pageParam.size,
                order,
                orderBy,
            });
        },
        {
            getNextPageParam: (lastPage, allPages) => {
                const pages = allPages.length;
                const hasMore = lastPage.counter ? pages * size < lastPage.counter : false;
                return hasMore ? { page: pages + 1, size } : undefined;
            },
            getPreviousPageParam: (_, allPages) => (allPages.length > 1 ? { page: allPages.length - 1, size } : undefined),
        },
    );
};

export const useRecoverInvestorLedger = (investorInfoId: number) => {
    const queryClient = useQueryClient();
    return useMutation<void, unknown, number>(['recover-investor-ledger', investorInfoId], async investorInfoId => {
        await recoverInvestorLedgerAsync(investorInfoId);
        queryClient.invalidateQueries({ queryKey: ['fetchLedgerEntries'] });
    });
};

export const useGenerateInvestorsLedgerStatements = (params: MaybeRef<{ month: Date; investorInfoId: number }>) => {
    const queryClient = useQueryClient();
    return useMutation(
        ['generateInvestorsLedgerStatements', unref(params).month, unref(params).investorInfoId],
        () => withLoading(() => generateInvestorsLedgerStatements(unref(params).month, unref(params).investorInfoId)),
        {
            onSuccess() {
                queryClient.invalidateQueries({ queryKey: ['getInvestorLedgerStatements', unref(params).investorInfoId] });
                showMessage('Investor ledger statements generated', 'is-success');
            },
        },
    );
};

export const useGetInvestorLedgerStatements = (params: MaybeRef<{ year?: number; investorInfoId: number }>) => {
    const year = computed(() => unref(params).year);
    const investorInfoId = computed(() => unref(params).investorInfoId);
    const enabled = computed(() => !!unref(params).investorInfoId);

    return useQuery(
        ['getInvestorLedgerStatements', investorInfoId, year],
        () => withLoading(() => getInvestorLedgerStatements(unref(investorInfoId), unref(year))),
        { enabled },
    );
};

export const useGetTenantLedgerStatements = (params: MaybeRef<{ year?: number; rentTenancyId: number }>) => {
    return useQuery(['getTenantLedgerStatements', unref(params).rentTenancyId, unref(params).year], () =>
        withLoading(() => getTenantLedgerStatements(unref(params).rentTenancyId, unref(params).year)),
    );
};
export const useGetNamedBankAccount = (name: MaybeRef<string>, expandOpts?: MaybeRef<BankAccountExpandOpts>) => {
    return useQuery(['getNamedBankAccount', name], () => withLoading(() => getNamedBankAccount(unref(name), unref(expandOpts))), {
        enabled: computed(() => !!unref(name)),
    });
};
